import { humanizeValidationDetail } from '@/shared/utils/validationText';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  clearSession,
  getActiveTenantId,
  isLoggedIn,
  readCsrfToken,
  setActiveTenant,
  setSession,
} from '@/auth/store';
import { showToastImperative } from '@/components/ToastProvider';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  // ч.1397: сесія у httpOnly-cookie — браузер додає її сам.
  withCredentials: true,
});

const SAFE_METHODS = new Set(['get', 'head', 'options']);

api.interceptors.request.use((config) => {
  // FormData (multipart upload, 2026-08-13): глобальний JSON Content-Type
  // ламає boundary — прибираємо, axios/браузер поставлять правильний.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  // ч.1397: токенів у JS немає — сервер бере їх із cookie; заголовок просить cookie-режим
  // на відповідях входу/оновлення. Зміна даних — із CSRF-токеном (подвійна відправка).
  config.headers['X-Session'] = 'cookie';
  if (!SAFE_METHODS.has((config.method ?? 'get').toLowerCase())) {
    const csrf = readCsrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  // Порція 3.0.5.1b: автоматично передаємо tenant context у всі запити.
  // Глобальні endpoint'и (login, signup, my-tenants, create-tenant,
  // invitations/info, invitations/accept) самі ігнорують header — це робить
  // backend, не фронт.
  // ч.1406: запит, що сам назвав акаунт (дія над організацією іншого ВЛАСНОГО акаунта зі сторінки
  // «Видалити організацію»), лишається з ним; решта — поточний акаунт.
  const tenantId = getActiveTenantId();
  const preset = typeof config.headers.get === 'function'
    ? config.headers.get('X-Tenant-Id') : (config.headers as Record<string, unknown>)['X-Tenant-Id'];
  if (tenantId && !preset) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

// Авто-оновлення при 401: сервер ротує пару за refresh-cookie (ч.1397). Не вийшло —
// чистимо ознаку сесії і ведемо на /login.
let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const resp = await axios.post('/api/v1/auth/refresh', undefined, {
      withCredentials: true, headers: { 'X-Session': 'cookie' },
    });
    setSession((resp.data as { user_id?: string | null } | undefined)?.user_id ?? null);
    return true;
  } catch {
    clearSession();
    return false;
  }
}

/**
 * Перевірка чи відповідь — це випадок «суспендований/видалений tenant'ний
 * доступ». Backend (deps.get_tenant_context) повертає 403 з detail
 * "No active membership in this tenant" коли user має валідний access_token,
 * але його membership у X-Tenant-Id вже не ACTIVE.
 *
 * Поведінка фронта (за спецою 3.0.5 розд. 2.4):
 * 1. Скидаємо active_tenant_id (бо він тепер невалідний).
 * 2. Чистимо React Query cache (зробить вищий рівень — тут не маємо доступу
 *    до queryClient'а; передадимо подію через `notifyTenantContextLost`).
 * 3. Redirect на /select-tenant з повідомленням.
 */
function isMembershipLostError(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const detail = (error.response.data as { detail?: string } | undefined)?.detail;
  return typeof detail === 'string' && detail.toLowerCase().includes('membership');
}

/**
 * Перевірка чи 403 — це «недостатньо прав» від permission-enforcer'а
 * (Порція 3.0.7). Backend повертає detail вигляду:
 *   «Недостатньо прав. Потрібно: sales.realization.create»
 *
 * Поведінка фронта: показуємо alert з зрозумілим повідомленням. Це
 * додає UX-feedback навіть якщо кнопка не обгорнута <RequirePermission>
 * (для випадків коли user якось дійшов до неї). Sidebar + AutoRouteGuard
 * уже ховають доступ до сторінок, тому 403 «нема прав» — рідкісний кейс.
 */
/**
 * ч.1074: відмова політикою власника акаунта (обовʼязковий двофакторний вхід
 * або довірені мережі) — бекенд ставить заголовок X-Access-Block.
 */
export function accessBlockReason(error: AxiosError): string | null {
  if (error.response?.status !== 403) return null;
  const h = error.response.headers as Record<string, unknown> | undefined;
  const v = h?.['x-access-block'];
  return typeof v === 'string' && v ? v : null;
}

function isInsufficientPermissionError(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const detail = (error.response.data as { detail?: string } | undefined)?.detail;
  return typeof detail === 'string' && detail.startsWith('Недостатньо прав');
}

/**
 * ч.1119: відмова ролі «лише перегляд». Бекенд (read_only_guard.READ_ONLY_DETAIL)
 * відповідає 403 з текстом, що починається цими словами.
 */
export const READ_ONLY_PREFIX = 'Ваша роль дозволяє лише перегляд';

function isReadOnlyRoleError(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const detail = (error.response.data as { detail?: string } | undefined)?.detail;
  return typeof detail === 'string' && detail.startsWith(READ_ONLY_PREFIX);
}

/**
 * Фонові запити сторінок («позначити переглянутим», «оновити виписку при
 * відкритті»): користувач нічого не натискав, тож відмову ролі перегляду
 * показувати тостом не треба — сторінка просто лишається як є.
 */
const BACKGROUND_WRITE = /\/(viewed|bank-api\/refresh)$/;

let lastReadOnlyToastAt = 0;
let lastDeniedToastAt = 0;
let lastPaymentToastAt = 0;

/**
 * Деякі endpoint'и обробляють 422 локально (показують inline у формі)
 * і НЕ хочуть глобального toast. Маркер — config.headers['X-Silent-422'].
 * За замовчуванням toast показується для всіх 422.
 */
function isMutationSilenced(error: AxiosError): boolean {
  const config = error.config;
  if (!config?.headers) return false;
  const h = config.headers as Record<string, unknown>;
  // ч.892: X-Silent-Toast глушить і успіхи, і помилки (фонові запити: ui-prefs).
  return Boolean(h['X-Silent-422'] || h['X-Silent-Toast']);
}

/**
 * ч.890 (рішення власника): УСІ системні повідомлення — тостом справа вгорі,
 * автоприховування 5 с. Не лише помилки, а й «збережено» / «проведено» /
 * «розпроведено» / «видалено» / «платіжні документи створено» — раніше
 * сторінки писали це банерами внизу форми, яких користувач не бачить.
 * Робимо ЦЕНТРАЛЬНО в інтерцепторі (не по 50 редакторах): текст — за URL
 * і методом; «збережено» — коли відповідь схожа на документ/картку
 * (є id + document_number/invoice_number/posted/deletion_mark/document_date),
 * щоб не тостити POST-прев'ю/розрахунки. Заголовок `X-Silent-Toast` вимикає.
 */
const SYSTEM_TOAST_MS = 5000;
const MUTATING = new Set(['post', 'patch', 'put', 'delete']);

function successToastFor(r: { config: InternalAxiosRequestConfig; data: unknown }): string | null {
  const method = (r.config.method ?? '').toLowerCase();
  if (!MUTATING.has(method)) return null;
  if ((r.config.headers as Record<string, unknown> | undefined)?.['X-Silent-Toast']) return null;
  const url = (r.config.url ?? '').split('?')[0] ?? '';
  if (/\/(auth|login|logout|refresh)\b/.test(url)) return null;
  // ч.1120: документ «Закриття місяця» — «проведено» кажемо лише тоді, коли
  // місяць справді закрито. Якщо перевірка не пустила або крок упав, причину
  // показує сама сторінка документа, а не тост «проведено».
  if (/\/month-close-runs\/(run|[^/]+\/(post|repost|retry))$/.test(url)) {
    const st = (r.data as { status?: string } | null)?.status;
    if (st === 'COMPLETED') return 'місяць закрито';
    if (st === 'PARTIAL') return 'закрито частково — є кроки з помилкою';
    return null;
  }
  if (/\/(post)$/.test(url)) return 'проведено';
  if (/\/(unpost)$/.test(url)) return 'розпроведено';
  if (/\/cancel$/.test(url)) return 'скасовано';
  if (/\/(create-bank-payments|create-cash-orders|create-payment-orders|make-docs)$/.test(url)) {
    return 'платіжні документи створено';
  }
  if (/\/close-sheet$/.test(url)) return 'відомість закрито — створено депонування';
  if (method === 'delete') return 'видалено';
  const d = r.data as Record<string, unknown> | null;
  if (d && typeof d === 'object' && !Array.isArray(d) && 'id' in d
    && ('document_number' in d || 'invoice_number' in d || 'posted' in d
      || 'deletion_mark' in d || 'document_date' in d)) {
    return 'збережено';
  }
  return null;
}

api.interceptors.response.use(
  (r) => {
    const msg = successToastFor(r);
    if (msg) showToastImperative({ type: 'success', message: msg, durationMs: SYSTEM_TOAST_MS });
    return r;
  },
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 — refresh JWT.
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh')
    ) {
      original._retry = true;
      refreshPromise ??= refreshSession().finally(() => {
        refreshPromise = null;
      });
      if (await refreshPromise) {
        return api.request(original);
      }
      // не получилось — на логин. ч.1073: зі сторінки запрошення після
      // входу повертаємось на неї (інакше запрошення «губиться»).
      window.location.href = window.location.pathname === '/accept-invitation'
        ? `/login?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`
        : '/login';
      return Promise.reject(error);
    }

    // ч.1074: акаунт не пускає політика власника (2FA / мережа) — на сторінку
    // вибору акаунта: там пояснення і дії (підтвердити кодом, налаштувати 2FA,
    // звернутися до власника). Тост не показуємо — сторінка скаже сама.
    const block = accessBlockReason(error);
    if (block) {
      const blockedTenant = getActiveTenantId();
      setActiveTenant(null);
      if (!window.location.pathname.startsWith('/select-tenant')) {
        const q = new URLSearchParams({ reason: 'access_blocked' });
        if (blockedTenant) q.set('tenant', blockedTenant);
        window.location.href = `/select-tenant?${q.toString()}`;
      }
      return Promise.reject(error);
    }

    // 403 з «No active membership» — tenant втратив активність.
    if (isMembershipLostError(error)) {
      // Скидаємо лише tenant — JWT валідний, user не повинен заново логінитись.
      setActiveTenant(null);
      // Full reload на /select-tenant: гарантує перечитування /my-tenants
      // і очищення React Query cache без явного доступу до queryClient.
      // Параметр reason=membership_lost дозволить сторінці показати тост.
      if (!window.location.pathname.startsWith('/select-tenant')) {
        window.location.href = '/select-tenant?reason=membership_lost';
      }
    }

    // 403 «Недостатньо прав» (Порція 3.0.7, UX γ-essential 2026-05-21) —
    // global UX feedback через toast. Більшість admin-дій уже сховано
    // через Sidebar + AutoRouteGuard + <RequirePermission> + UX-α/β
    // org-dropdown фільтр. Якщо user усе ж дійшов до 403 (наприклад
    // stale cache /auth/me після зміни прав adminом, або форма без
    // обгортки) — показуємо toast з detail з backend response. БЕЗ
    // перемикача org (поле залишається відкритим — user обирає правильну
    // org вручну і ретайнить).
    // ч.1311 (власник: «зашёл под менеджером по продажам — везде красный бейдж»): фонові
    // ЧИТАННЯ (віджети головної, довідники для полів редактора) під роллю без права
    // відповідають 403 — це не дія користувача, сторінка показує свій стан. Тост — лише на
    // дію (POST/PATCH/PUT/DELETE), не частіше ніж раз на 3 с.
    if (isInsufficientPermissionError(error)) {
      const method = (original?.method ?? 'get').toLowerCase();
      const now = Date.now();
      if (method !== 'get' && method !== 'head' && !isMutationSilenced(error)
        && now - lastDeniedToastAt > 3000) {
        lastDeniedToastAt = now;
        const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
        showToastImperative({
          type: 'error',
          title: '⚠ Немає права на операцію',
          message: detail ?? 'Недостатньо прав для цієї дії.',
          durationMs: SYSTEM_TOAST_MS,
        });
      }
    }

    // ч.1119: роль «лише перегляд» — пояснюємо, а не «помилка (403)». Фонові
    // запити сторінок і запити з X-Silent-Toast — мовчки; не частіше ніж раз
    // на 3 с (сторінка може послати кілька запитів одразу).
    const readOnlyRefusal = isReadOnlyRoleError(error);
    if (readOnlyRefusal) {
      const url = (original?.url ?? '').split('?')[0] ?? '';
      const now = Date.now();
      if (!isMutationSilenced(error) && !BACKGROUND_WRITE.test(url)
        && now - lastReadOnlyToastAt > 3000) {
        lastReadOnlyToastAt = now;
        showToastImperative({
          type: 'warning',
          title: 'Лише перегляд',
          message: (error.response?.data as { detail?: string } | undefined)?.detail
            ?? 'Ваша роль дозволяє лише перегляд.',
          durationMs: SYSTEM_TOAST_MS,
        });
      }
    }

    // ч.1398: 402 — підписка бази (лише перегляд / недоступна) або брак коштів на балансі.
    // Пояснюємо текстом сервера; фонові читання під заблокованою базою — мовчки (банер у
    // шапці скаже сам), тост — лише на дію і не частіше ніж раз на 3 с.
    if (error.response?.status === 402 && !isMutationSilenced(error)) {
      const method = (original?.method ?? 'get').toLowerCase();
      const now = Date.now();
      if (method !== 'get' && method !== 'head' && now - lastPaymentToastAt > 3000) {
        lastPaymentToastAt = now;
        const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
        showToastImperative({
          type: 'warning',
          title: 'Потрібна оплата',
          message: typeof detail === 'string' ? detail : 'Дія недоступна до оплати тарифу.',
          durationMs: SYSTEM_TOAST_MS,
        });
      }
    }

    // 409 Conflict (наприклад, UNIQUE constraint violation) — показуємо
    // detail з backend. Власник 2026-06-02: «duplicate key value violates
    // unique constraint uq_organizations_tenant_code» було 500 у БД,
    // переведено у 409 з людиночитаним повідомленням.
    if (error.response?.status === 409 && !isMutationSilenced(error)) {
      const detail = (error.response.data as { detail?: string } | undefined)?.detail;
      showToastImperative({
        type: 'error',
        title: '⚠ Конфлікт',
        message: detail ?? 'Запис з такими ключовими значеннями вже існує.',
        durationMs: SYSTEM_TOAST_MS,
      });
    }

    // 500 Internal Server Error — показуємо короткий toast щоб клієнт не
    // думав «нічого не відбувається». Деталі — у docker logs.
    if (error.response?.status === 500 && !isMutationSilenced(error)) {
      showToastImperative({
        type: 'error',
        title: '⚠ Серверна помилка',
        message: 'Сервер не зміг обробити запит. Спробуйте ще раз або зверніться у підтримку.',
        durationMs: SYSTEM_TOAST_MS,
      });
    }

    // ч.890: решта помилок мутацій (400/404/інші 403 тощо) — теж тостом з detail,
    // а не лише червоним рядком унизу сторінки.
    {
      const st = error.response?.status;
      const method = (original?.method ?? '').toLowerCase();
      if (st && MUTATING.has(method) && ![401, 402, 409, 422, 500].includes(st)
        && !isInsufficientPermissionError(error) && !isMembershipLostError(error)
        && !readOnlyRefusal
        && !isMutationSilenced(error)) {
        const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
        showToastImperative({
          type: 'error',
          title: `⚠ Помилка (${st})`,
          message: typeof detail === 'string' ? detail : 'Дію не виконано. Перевірте дані.',
          durationMs: SYSTEM_TOAST_MS,
        });
      }
    }

    // ч.1343: список помилок перевірки (422) → один зрозумілий рядок ДО того, як
    // помилка дійде до сторінки. Раніше ~40 редакторів малювали цей список у
    // `{error}` — React падав, і замість повідомлення була «непередбачена
    // помилка». Сам список лишається в `detail_items`.
    if (error.response?.status === 422) {
      const data = error.response.data as { detail?: unknown; detail_items?: unknown } | undefined;
      if (data && Array.isArray(data.detail)) {
        data.detail_items = data.detail;
        data.detail = humanizeValidationDetail(data.detail);
      }
    }

    // 422 Pydantic ValidationError — показуємо детальне повідомлення.
    // Власник 2026-06-02: «створюю Організацію — фоп на загальній — все
    // заповнив — не зберігає» — UI мовчки ковтав 422, тому що ця гілка
    // показувала тільки 403. Тепер ловимо обидва.
    if (error.response?.status === 422 && !isMutationSilenced(error)) {
      const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
      const message = typeof detail === 'string' && detail
        ? detail : 'Невірні дані. Перевірте форму.';
      showToastImperative({
        type: 'error',
        title: '⚠ Помилка валідації',
        message,
        durationMs: SYSTEM_TOAST_MS,
      });
    }

    return Promise.reject(error);
  },
);
